/**
 * usePeriodNavigation
 * Hook that extracts period navigation logic from PeriodNavigator.
 * Navigates prev/next across ALL periods (not year-filtered).
 * Auto-initializes to currentPeriod on mount when URL has no period.
 */

import { useEffect, useMemo, useRef } from 'react';
import type {
  GetPotPeriodsResponse,
  PotPeriodSummaryDto,
} from '../api/potPeriodsApi';

interface PeriodsByYear {
  year: number;
  periods: PotPeriodSummaryDto[];
}

interface UsePeriodNavigationOptions {
  periodData: GetPotPeriodsResponse | undefined;
  selectedPeriodId: string | null;
  onPeriodChange: (periodId: string) => void;
}

export function usePeriodNavigation({
  periodData,
  selectedPeriodId,
  onPeriodChange,
}: UsePeriodNavigationOptions) {
  const pendingPeriodRef = useRef<string | null>(null);

  // Stabilise the periods array reference so effects and memos don't re-run
  // on every render when periodData is undefined (avoids new [] each time).
  const allPeriods = useMemo(
    () => periodData?.periods ?? [],
    [periodData?.periods]
  );

  // Use pending period if URL hasn't updated yet, otherwise use URL period
  const effectivePeriodId = pendingPeriodRef.current ?? selectedPeriodId;

  // Auto-initialize to currentPeriod when URL has no period
  useEffect(() => {
    if (!allPeriods.length) return;

    if (selectedPeriodId) {
      // URL has period — clear pending
      pendingPeriodRef.current = null;
    } else if (!pendingPeriodRef.current) {
      // No URL period and no pending — auto-initialize
      const periodToSelect = periodData?.currentPeriod ?? allPeriods[0];
      if (periodToSelect) {
        pendingPeriodRef.current = periodToSelect.id;
        onPeriodChange(periodToSelect.id);
      }
    }
  }, [allPeriods, periodData?.currentPeriod, selectedPeriodId, onPeriodChange]);

  // Period ID that's confirmed valid in the loaded periods. Null when periods
  // haven't loaded yet or the current ID is stale. Use this for data queries
  // to avoid firing requests with invalid period IDs.
  const validatedPeriodId = useMemo(() => {
    const id = effectivePeriodId;
    if (!id || !allPeriods.length) return null;
    return allPeriods.some(p => p.id === id) ? id : null;
  }, [effectivePeriodId, allPeriods]);

  // Prev/next navigate across ALL periods (desc sorted: index+1 = older)
  const handlePrev = () => {
    if (!allPeriods.length || !effectivePeriodId) return;
    const idx = allPeriods.findIndex(p => p.id === effectivePeriodId);
    if (idx < 0) return;
    if (idx < allPeriods.length - 1) {
      const next = allPeriods[idx + 1];
      pendingPeriodRef.current = next.id;
      onPeriodChange(next.id);
    }
  };

  const handleNext = () => {
    if (!allPeriods.length || !effectivePeriodId) return;
    const idx = allPeriods.findIndex(p => p.id === effectivePeriodId);
    if (idx > 0) {
      const prev = allPeriods[idx - 1];
      pendingPeriodRef.current = prev.id;
      onPeriodChange(prev.id);
    }
  };

  const canGoPrev = (() => {
    if (!allPeriods.length || !effectivePeriodId) return false;
    const idx = allPeriods.findIndex(p => p.id === effectivePeriodId);
    return idx >= 0 && idx < allPeriods.length - 1;
  })();

  const canGoNext =
    allPeriods.length > 0 && effectivePeriodId
      ? allPeriods.findIndex(p => p.id === effectivePeriodId) > 0
      : false;

  // Group periods by year for the popover
  const periodsByYear = useMemo<PeriodsByYear[]>(() => {
    if (!allPeriods.length) return [];
    const map = new Map<number, PotPeriodSummaryDto[]>();
    for (const p of allPeriods) {
      const list = map.get(p.year) ?? [];
      list.push(p);
      map.set(p.year, list);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => b - a)
      .map(([year, periods]) => ({ year, periods }));
  }, [allPeriods]);

  // Selected period object
  const selectedPeriod = useMemo(
    () => allPeriods.find(p => p.id === effectivePeriodId) ?? null,
    [allPeriods, effectivePeriodId]
  );

  // Full label: "Week 9, 2026 (Feb 23 – Mar 1)"
  const selectedPeriodLabel = selectedPeriod?.displayLabel ?? '';

  // Short label: "W9, 2026"
  const selectedPeriodShortLabel = selectedPeriod
    ? `W${selectedPeriod.weekNumber}, ${selectedPeriod.year}`
    : '';

  return {
    effectivePeriodId,
    validatedPeriodId,
    handlePrev,
    handleNext,
    canGoPrev,
    canGoNext,
    periodsByYear,
    selectedPeriod,
    selectedPeriodLabel,
    selectedPeriodShortLabel,
  };
}
